import {useEffect, useRef, useState} from 'react';

import {capitalize} from '@mui/material';
import axios, {AxiosError} from 'axios';
import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {HealthProviderLocation, NetworkTypesEnum, TypeLocalNameEnum} from 'domain/usecases';
import {GetHealthProviderLocationPreviewParams} from 'domain/usecases/private/booking-flow/health/get-health-provider-location-preview';
import {GetHealthProviderLocationSupportAccreditedParams} from 'domain/usecases/private/booking-flow/health/get-health-provider-location-support-accredited';
import {makeGetExamsProviderLocation} from 'main/factories/usecases/private/booking-flow/remote-exams-booking/get-exams-provider-location-factory';
import {
	makeGetHealthFavoriteProviderLocation,
	makeGetHealthProviderLocation as makeGetHealthProviderLocationV2,
} from 'main/factories/usecases/private/booking-flow/remote-health-booking';
import {makeGetHealthProviderLocationPreview} from 'main/factories/usecases/private/booking-flow/remote-health-booking/get-health-provider-location-preview-factory';
import {makeGetHealthProviderLocationSupportAccredited} from 'main/factories/usecases/private/booking-flow/remote-health-booking/get-health-provider-location-support-accredited-factory';
import {makePostOdontoProviders} from 'main/factories/usecases/private/booking-flow/remote-odonto-booking/post-odonto-providers-factory';
import {makePostProvidersDentalGuide} from 'main/factories/usecases/private/guide-flow/remote-dental-guide/post-providers-factory';
import {
	DisabilityEnum,
	FeaturesEnum,
	NearNeighborhoodEnum,
	OdontoGuideServicesTypeEnum,
	OperationStateTypesEnum,
	QueryParamsEnum,
} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {APIErrorsMessage} from 'presentation/pages/public';
import {removeDuplicates, sortByKey} from 'presentation/utils';

import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';

import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {EntryPointCodeEnum, FlowEnum, TelemedicineEnum} from '../../constants';
import {MANUAL_DISTANCE_ORIGIN} from 'presentation/modules/booking/shared/constants/unitsInfo';

import {
	KM_TO_REFETCH,
	MAX_DISTANCE_FILTER_UNITS,
	MAX_DISTANCE_FILTER_UNITS_ODONTO,
	MAX_DISTRICT_FILTER_COUNT,
	MIN_DISTANCE_IN_KM,
	MIN_DISTANCE_TO_FETCH_ODONTO_UNITS,
} from './constants';
import {UnitParams, UseBookingUnitsProps} from './types';
import {formatExamUnit} from './utils/formatExamUnit';
import {formatHealthUnit} from './utils/formatHealthUnit';
import {parseExamUnits} from './utils/parseExamUnits';
import {parseHealthUnits} from './utils/parseHealthUnits';

import {Units} from 'presentation/modules/booking/shared/store/useUnitsListStore/types';
import {useUnitsFetchStateStore} from 'presentation/modules/booking/shared/store/useUnitsFetchStateStore';
import {useFilteredNetworkList} from 'presentation/modules/booking/shared/hooks/useFilteredNetworkList';
import {useRequestSchedulePermission} from 'presentation/modules/booking/shared/hooks/useRequestSchedulePermission';
import {parseOdontoUnits} from 'presentation/modules/booking/odonto/utils/parseOdontoUnits';
import {parseOdontoSupportNetworkUnits} from 'presentation/modules/booking/odonto/utils/parseOdontoSupportNetworkUnits';
import {parseOdontoDistrictFilter} from 'presentation/modules/booking/odonto/utils/parseOdontoDistrictFilter';

export function useBookingUnits({flowType}: UseBookingUnitsProps) {
	const {isGndiSaoPaulo} = useHostName();
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const [notFoundErrorState, setNotFoundErrorState] = useState(false);
	const [specialtyNotAvailableOnlineState, setSpecialtyNotAvailableOnlineState] = useState(false);
	const [hasUnitsUpToMaxDistanceState, setHasUnitsUpToMaxDistanceState] = useState(true);
	const [isPreviewUnitsFetchedState, setIsPreviewUnitsFetchedState] = useState(false);
	const operationState = useUnitsFetchStateStore(state => state.unitListFetchState);
	const setOperationState = useUnitsFetchStateStore(state => state.setUnitListFetchState);
	const unitResponseState = useUnitsListStore(state => state.unitsList);
	const setUnitResponseState = useUnitsListStore(state => state.setUnitsList);
	const abortControllerRef = useRef<AbortController | null>(null);
	const {
		setSearchParams,
		SEARCH_PARAMS: {query, localityDistance, distanceOrigin},
	} = useQueryParams();
	const {
		handleGetHealthRequestSchedulePermission,
		healthRequestSchedulePermissionState,
		resetHealthRequestScheduleState,
	} = useRequestSchedulePermission();
	const isHealthFlow = flowType === FlowEnum.HEALTH;
	const {isEnabled: isUnitsNetworksEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_UNITS_NETWORKS,
	);
	const isManualDistanceSearch =
		isUnitsNetworksEnabled && distanceOrigin === MANUAL_DISTANCE_ORIGIN;
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const isHealthOrExamFlow = isHealthFlow || isExamFlow;
	const {isEnabled: pacmanFlagIsEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_PACMAN);

	useEffect(
		() => () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
				abortControllerRef.current = null;
			}
		},
		[],
	);

	const IS_ERROR = operationState === OperationStateTypesEnum.ERROR;
	const IS_LOADING =
		operationState === OperationStateTypesEnum.SUBMITTING ||
		operationState === OperationStateTypesEnum.PENDING;
	const IS_DONE = operationState === OperationStateTypesEnum.DONE;

	const unitsByLocality = unitResponseState?.units?.unitsByLocality;
	const unitsByDistance = unitResponseState?.units?.unitsByDistance;

	const NOT_FOUND_DEFAULT =
		!unitResponseState ||
		(unitsByLocality?.length === 0 && unitsByDistance?.length === 0) ||
		notFoundErrorState;
	const NOT_FOUND_SUPPORT_ACCREDITED =
		unitResponseState?.units?.unitsSupportAccredited?.length === 0;

	const SHOW_NOT_FOUND =
		(isOdontoFlow && NOT_FOUND_DEFAULT) ||
		(isHealthOrExamFlow && NOT_FOUND_DEFAULT && NOT_FOUND_SUPPORT_ACCREDITED) ||
		(isHealthFlow && NOT_FOUND_DEFAULT && specialtyNotAvailableOnlineState);

	const hasOnlyTelemedicineUnit =
		isHealthFlow &&
		unitsByDistance?.length === 0 &&
		unitsByLocality?.length === 1 &&
		(unitsByLocality[0] as Units<FlowEnum.HEALTH>)?.telemedicine ===
			TelemedicineEnum.IS_TELEMEDICINE;

	const filteredNetworkList = useFilteredNetworkList(query);
	const filteredNetworkUnits = filteredNetworkList?.units;

	const FILTERED_LIST_IS_EMPTY =
		filteredNetworkUnits?.unitsByLocality?.length === 0 &&
		filteredNetworkUnits?.unitsByDistance?.length === 0 &&
		filteredNetworkUnits?.unitsSupportAccredited?.length === 0;

	const HAS_ANY_UNIT = Boolean(
		unitsByLocality?.length ||
		unitsByDistance?.length ||
		unitResponseState?.units?.unitsSupportAccredited?.length,
	);

	const ALLOWS_REQUEST_SCHEDULE_REGISTRATION = Boolean(
		healthRequestSchedulePermissionState?.allowsRegistration && pacmanFlagIsEnabled,
	);

	const SHOW_NOT_FOUND_STATE = isUnitsNetworksEnabled
		? (isHealthFlow && NOT_FOUND_DEFAULT && specialtyNotAvailableOnlineState) ||
			ALLOWS_REQUEST_SCHEDULE_REGISTRATION ||
			(Boolean(query) && FILTERED_LIST_IS_EMPTY && HAS_ANY_UNIT)
		: SHOW_NOT_FOUND || FILTERED_LIST_IS_EMPTY;
	const IS_SELF_NETWORK = unitResponseState?.networkType?.name === NetworkTypesEnum.SELF;
	const IS_NOT_SELF_NETWORK = !IS_SELF_NETWORK;

	const showSpecialtyNotAvailableInSelfNetwork = Boolean(
		hasOnlyTelemedicineUnit && unitsByLocality[0].isSpecialtyBlocked,
	);

	const getShouldShowEmptyAppointmentsCard = () => {
		if (showSpecialtyNotAvailableInSelfNetwork) return false;
		if (isGndiSaoPaulo) {
			return hasOnlyTelemedicineUnit && NOT_FOUND_SUPPORT_ACCREDITED;
		}

		return hasOnlyTelemedicineUnit;
	};

	const shouldShowEmptyAppointmentsCard = getShouldShowEmptyAppointmentsCard();

	const showSupportAccreditedNetwork = () => {
		if (isOdontoFlow) {
			return IS_NOT_SELF_NETWORK;
		}

		if (isHealthFlow) {
			if (isGndiSaoPaulo) {
				return true;
			}

			if (shouldShowEmptyAppointmentsCard) return false;

			return (
				((NOT_FOUND_DEFAULT && IS_SELF_NETWORK) || IS_NOT_SELF_NETWORK) &&
				!NOT_FOUND_SUPPORT_ACCREDITED &&
				(Number(localityDistance) >= Number(KM_TO_REFETCH) || !hasUnitsUpToMaxDistanceState)
			);
		}

		return false;
	};

	const shouldShowSupportAccreditedNetwork = showSupportAccreditedNetwork();

	const showSelfNetwork = () => {
		if (isOdontoFlow || isExamFlow) {
			return true;
		}

		if (isHealthFlow) {
			if (isGndiSaoPaulo) return true;

			return !shouldShowSupportAccreditedNetwork;
		}

		return false;
	};

	const shouldShowSelfNetwork = showSelfNetwork();

	const handleFetchUnits = async (params: UnitParams) => {
		const {cityName, specialtyCode, isUsingGeolocation, coordinates} = params;

		const isMissingCity =
			(!isUsingGeolocation && !cityName) || (isUsingGeolocation && !coordinates);

		if (isMissingCity || !specialtyCode) {
			setUnitsEmpty();

			setOperationState(OperationStateTypesEnum.DONE);
			return;
		}

		if (flowType === FlowEnum.ODONTO) {
			await handleFetchOdontoUnits(params);

			return;
		}

		if (flowType === FlowEnum.EXAM) {
			await handleFetchExamUnits(params);

			return;
		}

		if (flowType === FlowEnum.HEALTH) {
			await handleFetchHealthUnits(params);
		}
	};

	const handleFetchHealthUnits = async ({
		stateCode,
		cityName,
		providerCode,
		specialtyCode,
		subSpecialtyCode,
		typeLocalName,
		distanceFilterValue,
		districtName,
		citiesNames,
		coordinates,
		isUsingGeolocation,
		hasGeoDistrictsFilter,
		isFavoriteDoctorsFlow,
	}: UnitParams) => {
		if (!hasUnitsUpToMaxDistanceState && !hasGeoDistrictsFilter) return;

		setOperationState(OperationStateTypesEnum.SUBMITTING);
		setSpecialtyNotAvailableOnlineState(false);
		setNotFoundErrorState(false);

		abortControllerRef.current = new AbortController();

		const distanceFilterAtMaxDistance =
			Number(distanceFilterValue) >= MAX_DISTANCE_FILTER_UNITS;
		const distanceFilterAtMinimumDistance = distanceFilterValue === MIN_DISTANCE_IN_KM;
		const shouldFilterByDistricts = !distanceFilterValue || distanceFilterAtMinimumDistance;

		const unitParams = {
			specialtyCode: String(specialtyCode),
			subSpecialtyCode: subSpecialtyCode ? String(subSpecialtyCode) : '',
			stateCode: isUsingGeolocation && !hasGeoDistrictsFilter ? '' : stateCode?.toUpperCase(),
			cityName:
				isUsingGeolocation && !hasGeoDistrictsFilter
					? coordinates
					: cityName?.toUpperCase(),
			distance:
				distanceFilterAtMinimumDistance || !distanceFilterValue
					? undefined
					: distanceFilterValue,
			typeLocalName: hasGeoDistrictsFilter
				? TypeLocalNameEnum.CIDADE
				: typeLocalName || TypeLocalNameEnum.CIDADE,
			entryPointCode: Number(EntryPointCodeEnum.V2),
			districtNames: shouldFilterByDistricts ? districtName?.toUpperCase() : undefined,
			citiesNames: shouldFilterByDistricts ? citiesNames : undefined,
			signal: abortControllerRef.current?.signal,
		};

		const unitFavoriteProviderLocationParams = {
			specialtyCode: String(specialtyCode),
			subSpecialtyCode: subSpecialtyCode ? String(subSpecialtyCode) : '',
			providerCode: providerCode ? String(providerCode) : '',
			entryPointCode: EntryPointCodeEnum.V2,
			signal: abortControllerRef.current?.signal,
		};

		const requestSchedulePermissionParams = {
			city: cityName?.toUpperCase(),
			stateCode,
			specialtyCode,
			typeLocalName: isUsingGeolocation
				? TypeLocalNameEnum.CIDADE
				: typeLocalName || TypeLocalNameEnum.CIDADE,
			signal: abortControllerRef.current?.signal,
		};

		const checkHealthUnitsUpToMaxDistanceParams = {
			cityName:
				isUsingGeolocation && !hasGeoDistrictsFilter
					? coordinates
					: cityName?.toUpperCase(),
			entryPointCode: unitParams.entryPointCode,
			specialtyCode,
			stateCode: isUsingGeolocation && !hasGeoDistrictsFilter ? '' : stateCode?.toUpperCase(),
			subSpecialtyCode,
			typeLocalName: hasGeoDistrictsFilter
				? TypeLocalNameEnum.CIDADE
				: typeLocalName || TypeLocalNameEnum.CIDADE,
			distance: MAX_DISTANCE_FILTER_UNITS.toString(),
			signal: abortControllerRef.current?.signal,
		};

		try {
			resetHealthRequestScheduleState();

			await handleFetchHealthSupportAccreditedUnits({
				cityName: cityName?.toUpperCase(),
				specialtyCode,
				stateCode: stateCode?.toUpperCase(),
				subSpecialtyCode,
				typeLocalName: isUsingGeolocation
					? TypeLocalNameEnum.CIDADE
					: typeLocalName || TypeLocalNameEnum.CIDADE,
				signal: abortControllerRef.current?.signal,
			});

			const isUnitFavoriteProviderLocationParamsValid = Boolean(
				unitFavoriteProviderLocationParams.specialtyCode &&
				unitFavoriteProviderLocationParams.providerCode &&
				unitFavoriteProviderLocationParams.entryPointCode,
			);

			const units =
				isUnitFavoriteProviderLocationParamsValid && isFavoriteDoctorsFlow
					? await makeGetHealthFavoriteProviderLocation().get(
							unitFavoriteProviderLocationParams,
						)
					: await makeGetHealthProviderLocationV2().get(unitParams);

			const listByLocality = units?.unidadesPorLocal;
			const listByDistance = units?.unidadesPorRaio;

			const isSelfNetworkAndLocalityListEmpty =
				listByLocality?.length === 0 && units?.rede === NetworkTypesEnum.SELF;
			const isSelfNetworkAndDistanceListEmpty =
				listByDistance?.length === 0 && units?.rede === NetworkTypesEnum.SELF;
			const isNotSelfNetworkAndListByDistanceEmpty =
				listByDistance?.length === 0 && units?.rede !== NetworkTypesEnum.SELF;
			const emptyUnits =
				!units || (listByLocality?.length === 0 && listByDistance?.length === 0);

			const listByLocalityOnlyTele =
				listByLocality?.length > 0 &&
				listByLocality?.every(
					item => item?.telemedicina === TelemedicineEnum.IS_TELEMEDICINE,
				);

			const shoulCheckUnitsUpToMaxDistance =
				!isPreviewUnitsFetchedState && emptyUnits && !hasGeoDistrictsFilter;

			if (shoulCheckUnitsUpToMaxDistance) {
				const hasUnitsUpToMaxDistance = await checkHealthUnitsUpToMaxDistance(
					checkHealthUnitsUpToMaxDistanceParams,
				);

				if (!hasUnitsUpToMaxDistance) {
					if (pacmanFlagIsEnabled) {
						await handleGetHealthRequestSchedulePermission(
							requestSchedulePermissionParams,
						);
					}

					setOperationState(OperationStateTypesEnum.DONE);
					return;
				}
			}

			const shouldRefetchHealthWithTwentyFiveKm =
				(!units ||
					(isSelfNetworkAndLocalityListEmpty && !isUsingGeolocation) ||
					(listByLocalityOnlyTele && !isUsingGeolocation) ||
					(isUsingGeolocation && emptyUnits) ||
					(isUsingGeolocation &&
						listByLocalityOnlyTele &&
						isSelfNetworkAndDistanceListEmpty) ||
					isNotSelfNetworkAndListByDistanceEmpty) &&
				(!distanceFilterValue || distanceFilterValue === MIN_DISTANCE_IN_KM);

			if (shouldRefetchHealthWithTwentyFiveKm) {
				setRefetchSearchParams();

				return;
			}

			const hasNoSelfNetworkUnits =
				emptyUnits ||
				isNotSelfNetworkAndListByDistanceEmpty ||
				(listByLocalityOnlyTele && isSelfNetworkAndDistanceListEmpty);

			const shouldExpandToMaxDistance =
				isUnitsNetworksEnabled &&
				!isManualDistanceSearch &&
				!distanceFilterAtMaxDistance &&
				hasNoSelfNetworkUnits;

			if (shouldExpandToMaxDistance) {
				setMaxDistanceSearchParams();

				return;
			}

			const shouldRequestHealthSchedulePermission =
				pacmanFlagIsEnabled && distanceFilterAtMaxDistance && emptyUnits;

			if (shouldRequestHealthSchedulePermission) {
				await handleGetHealthRequestSchedulePermission(requestSchedulePermissionParams);
			}

			if (!units) {
				setUnitResponseState(prev => ({
					networkType: {
						code: '',
						name: NetworkTypesEnum.SELF,
					},
					units: {
						...prev.units,
						unitsByLocality: [],
						unitsByDistance: [],
					},
				}));
				setOperationState(OperationStateTypesEnum.DONE);

				return;
			}

			const isAccreditedV2Network = units?.rede === NetworkTypesEnum.ACCREDITED;

			const parsedUnitsByLocality = listByLocality
				? parseHealthUnits({units: listByLocality, isAccreditedV2Network})
				: [];
			const parsedUnitsByDistance = listByDistance
				? parseHealthUnits({units: listByDistance, isAccreditedV2Network})
				: [];

			const formattedHealthUnitsByLocality = formatHealthUnit(parsedUnitsByLocality, 'name');
			const formattedHealthUnitsByDistance = formatHealthUnit(
				parsedUnitsByDistance,
				'distance',
			);

			setUnitResponseState(prev => ({
				networkType: {
					code: '',
					name:
						units.rede === NetworkTypesEnum.SELF
							? NetworkTypesEnum.SELF
							: NetworkTypesEnum.SUPPORT,
				},
				units: {
					...prev.units,
					unitsByLocality: formattedHealthUnitsByLocality,
					unitsByDistance: formattedHealthUnitsByDistance,
				},
			}));

			setOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			if (error instanceof APIError && error.statusCode === HttpStatusCode.unprocessable) {
				setNotFoundErrorState(true);

				if (
					error?.message === APIErrorsMessage.NOT_ALLOWED_FOR_SPECIALTY &&
					flowType === FlowEnum.HEALTH
				) {
					setSpecialtyNotAvailableOnlineState(true);
					setHideSearchBarState(true);

					setOperationState(OperationStateTypesEnum.DONE);
					return;
				}

				const shouldRequestHealthSchedulePermission = pacmanFlagIsEnabled;

				const shoulCheckHealthUnitsUpToMaxDistance =
					!isPreviewUnitsFetchedState && !hasGeoDistrictsFilter;

				if (shoulCheckHealthUnitsUpToMaxDistance) {
					const hasUnitsUpToMaxDistance = await checkHealthUnitsUpToMaxDistance(
						checkHealthUnitsUpToMaxDistanceParams,
					);

					if (!hasUnitsUpToMaxDistance) {
						if (shouldRequestHealthSchedulePermission) {
							await handleGetHealthRequestSchedulePermission(
								requestSchedulePermissionParams,
							);
						}

						setOperationState(OperationStateTypesEnum.DONE);

						return;
					}
				}

				const shouldRefetchWithTwentyFiveKm =
					!distanceFilterValue || distanceFilterValue === MIN_DISTANCE_IN_KM;

				if (shouldRefetchWithTwentyFiveKm) {
					setRefetchSearchParams();
					return;
				}

				if (shouldRequestHealthSchedulePermission && distanceFilterAtMaxDistance) {
					await handleGetHealthRequestSchedulePermission(requestSchedulePermissionParams);
				}

				if (isUsingGeolocation) {
					setUnitResponseState(prev => ({
						networkType: {
							code: '',
							name: NetworkTypesEnum.SELF,
						},
						units: {
							...prev.units,
							unitsByLocality: [],
							unitsByDistance: [],
						},
					}));
				}

				setOperationState(OperationStateTypesEnum.DONE);
				return;
			}

			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const handleFetchExamUnits = async ({
		stateCode,
		cityName,
		specialtyCode,
		typeLocalName,
		distanceFilterValue,
		districtName,
		citiesNames,
		coordinates,
		isUsingGeolocation,
		hasGeoDistrictsFilter,
	}: UnitParams) => {
		setOperationState(OperationStateTypesEnum.SUBMITTING);
		setNotFoundErrorState(false);
		const distanceFilterAtMinimumDistance = distanceFilterValue === MIN_DISTANCE_IN_KM;
		const shouldFilterByDistricts = !distanceFilterValue || distanceFilterAtMinimumDistance;

		try {
			abortControllerRef.current = new AbortController();

			const units = await makeGetExamsProviderLocation().get({
				examType: specialtyCode,
				stateCode:
					isUsingGeolocation && !hasGeoDistrictsFilter ? '' : stateCode?.toUpperCase(),
				cityName:
					isUsingGeolocation && !hasGeoDistrictsFilter
						? coordinates
						: cityName?.toUpperCase(),
				distance:
					distanceFilterValue === MIN_DISTANCE_IN_KM || !distanceFilterValue
						? undefined
						: distanceFilterValue,
				typeLocalName: hasGeoDistrictsFilter
					? TypeLocalNameEnum.CIDADE
					: typeLocalName || TypeLocalNameEnum.CIDADE,
				districtNames: shouldFilterByDistricts ? districtName : undefined,
				citiesNames: shouldFilterByDistricts ? citiesNames : undefined,
				signal: abortControllerRef.current.signal,
			});

			const localityListEmpty = units?.unidadesPorLocal?.length === 0;
			const distanceListEmpty = units?.unidadesPorRaio?.length === 0;

			const shouldRefetchExamWithTwentyFiveKm =
				(!units ||
					(localityListEmpty && !isUsingGeolocation) ||
					(isUsingGeolocation && distanceListEmpty)) &&
				!distanceFilterValue;

			if (shouldRefetchExamWithTwentyFiveKm) {
				setRefetchSearchParams();
				return;
			}

			const emptyUnits =
				!units ||
				(units?.unidadesPorLocal?.length === 0 && units?.unidadesPorRaio?.length === 0);

			if (emptyUnits) {
				setUnitsEmpty();
				setOperationState(OperationStateTypesEnum.DONE);

				return;
			}

			const parsedUnitsByLocality = parseExamUnits(units.unidadesPorLocal);
			const parsedUnitsByDistance = units?.unidadesPorRaio
				? parseExamUnits(units.unidadesPorRaio)
				: [];

			const formattedUnitsByLocality = formatExamUnit(parsedUnitsByLocality, 'name');
			const formattedUnitsByDistance = formatExamUnit(parsedUnitsByDistance, 'distance');

			setUnitResponseState({
				networkType: {
					code: '',
					name: NetworkTypesEnum.SELF,
				},
				units: {
					unitsByLocality: formattedUnitsByLocality,
					unitsByDistance: formattedUnitsByDistance,
					unitsSupportAccredited: [],
				},
			});
			setOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			if (error instanceof APIError && error.statusCode === HttpStatusCode.unprocessable) {
				setNotFoundErrorState(true);

				const shouldRefetchWithTwentyFiveKm = !distanceFilterValue && !isUsingGeolocation;

				if (shouldRefetchWithTwentyFiveKm) {
					setRefetchSearchParams();
					return;
				}
			}
			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const handleFetchOdontoUnits = async ({
		stateCode,
		cityName,
		specialtyCode,
		subSpecialtyCode,
		includeNeighborhoodDistricts,
		districtName,
		disability,
		isUsingGeolocation,
		coordinates,
		distanceFilterValue,
		citiesNames,
		hasGeoDistrictsFilter,
		allDistrictsSelected,
	}: UnitParams) => {
		setOperationState(OperationStateTypesEnum.SUBMITTING);
		setNotFoundErrorState(false);

		const distanceFilterAtMinimumDistance = distanceFilterValue === MIN_DISTANCE_IN_KM;
		const districtFilter = parseOdontoDistrictFilter({
			districtName,
			citiesNames,
			stateCode,
		});

		const shouldSendEmptyDistrictFilter =
			districtFilter?.length > MAX_DISTRICT_FILTER_COUNT || allDistrictsSelected;

		const isUsingGeoAndHasNoDistrictsFilter = isUsingGeolocation && !hasGeoDistrictsFilter;
		const isUsingGeoAndHasDistrictsFilter = isUsingGeolocation && hasGeoDistrictsFilter;

		const getDistanceValue = () => {
			if (!isUsingGeolocation || isUsingGeoAndHasDistrictsFilter) {
				return undefined;
			}

			if (distanceFilterAtMinimumDistance || !distanceFilterValue) {
				return MIN_DISTANCE_TO_FETCH_ODONTO_UNITS;
			}

			return distanceFilterValue;
		};

		const commonOdontoParams = {
			cdEspecialidade: specialtyCode,
			tpLocalidade: isUsingGeoAndHasNoDistrictsFilter
				? TypeLocalNameEnum.COORDENADAS
				: TypeLocalNameEnum.CIDADE,
			nmLocal: isUsingGeoAndHasNoDistrictsFilter ? coordinates : undefined,
			distancia: getDistanceValue(),
		};

		try {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}

			abortControllerRef.current = new AbortController();

			const units = await makePostOdontoProviders().post({
				...commonOdontoParams,
				cdUf: isUsingGeoAndHasNoDistrictsFilter ? undefined : stateCode?.toUpperCase(),
				nmCidade: isUsingGeoAndHasNoDistrictsFilter ? undefined : cityName?.toUpperCase(),
				cdAtendimentoAcessoEspecial: disability || DisabilityEnum.ALL,
				cdSubEspecialidade: subSpecialtyCode,
				incluirBairrosVizinhos:
					includeNeighborhoodDistricts || NearNeighborhoodEnum.NOT_INCLUDE,
				filtroBairro: shouldSendEmptyDistrictFilter ? [] : districtFilter,
				signal: abortControllerRef.current.signal,
			});

			const shouldRefetchOdontoUnits =
				(!units || units?.unidades?.length === 0) &&
				(!isUnitsNetworksEnabled || !isManualDistanceSearch) &&
				isUsingGeoAndHasNoDistrictsFilter &&
				(!distanceFilterValue || distanceFilterValue === MIN_DISTANCE_IN_KM);

			if (shouldRefetchOdontoUnits) {
				setDistanceSearchParams(
					isUnitsNetworksEnabled
						? MAX_DISTANCE_FILTER_UNITS_ODONTO.toString()
						: KM_TO_REFETCH,
				);
				return;
			}

			if (!units || units?.unidades?.length === 0) {
				const supportNetworkResponse = await makePostProvidersDentalGuide().post({
					...commonOdontoParams,
					dsTipo: OdontoGuideServicesTypeEnum.OUTPATIENT,
					tpLocalidade: capitalize(commonOdontoParams.tpLocalidade.toLowerCase()),
					filtroBairro: shouldSendEmptyDistrictFilter
						? [
								{
									uf: stateCode?.toUpperCase(),
									cidade: cityName?.toUpperCase(),
									bairro: '',
								},
							]
						: districtFilter,
					signal: abortControllerRef.current?.signal,
				});

				if (!supportNetworkResponse) {
					setUnitsEmpty(NetworkTypesEnum.SELF);

					setOperationState(OperationStateTypesEnum.DONE);

					return;
				}

				const supportNetworkResponseParsedUnits =
					parseOdontoSupportNetworkUnits(supportNetworkResponse);

				const sortedUnitsByName =
					supportNetworkResponseParsedUnits &&
					sortByKey(supportNetworkResponseParsedUnits, 'name');
				const sortedUnitsByDistance =
					supportNetworkResponseParsedUnits &&
					sortByKey(supportNetworkResponseParsedUnits, 'distance');

				setUnitResponseState({
					networkType: {
						code: '',
						name: NetworkTypesEnum.SUPPORT,
					},
					units: {
						unitsByLocality: isUsingGeoAndHasNoDistrictsFilter
							? sortedUnitsByDistance
							: sortedUnitsByName,
						unitsByDistance: [],
						unitsSupportAccredited: [],
					},
				});

				setOperationState(OperationStateTypesEnum.DONE);
				return;
			}

			const parsedUnits = parseOdontoUnits(units);

			const filterDuplicate = removeDuplicates(parsedUnits);
			const sortedUnitsByName = filterDuplicate && sortByKey(filterDuplicate, 'name');
			const sortedUnitsByDistance = filterDuplicate && sortByKey(filterDuplicate, 'distance');

			setUnitResponseState({
				networkType: {
					code: '',
					name: NetworkTypesEnum.SELF,
				},
				units: {
					unitsByLocality: isUsingGeoAndHasNoDistrictsFilter
						? sortedUnitsByDistance
						: sortedUnitsByName,
					unitsByDistance: [],
					unitsSupportAccredited: [],
				},
			});

			setOperationState(OperationStateTypesEnum.DONE);
		} catch (error) {
			if (axios.isAxiosError(error) && error.code === AxiosError.ERR_CANCELED) {
				setOperationState(OperationStateTypesEnum.SUBMITTING);
				return;
			}

			if (error instanceof APIError && error.statusCode === HttpStatusCode.unprocessable) {
				setNotFoundErrorState(true);
			}
			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	const setUnitsEmpty = (networkType?: NetworkTypesEnum) => {
		setUnitResponseState({
			networkType: {code: '', name: networkType ?? ''},
			units: {
				unitsByLocality: [],
				unitsByDistance: [],
				unitsSupportAccredited: [],
			},
		});
	};

	const setDistanceSearchParams = (distance: string) => {
		const params = new URLSearchParams(window.location.search);
		params.set(QueryParamsEnum.LOCALITY_DISTANCE, distance);
		params.delete(QueryParamsEnum.DISTANCE_ORIGIN);
		params.delete(QueryParamsEnum.SEARCH_QUERY);
		setSearchParams(params);
	};

	const setRefetchSearchParams = () => {
		setDistanceSearchParams(KM_TO_REFETCH);
	};

	const setMaxDistanceSearchParams = () => {
		setDistanceSearchParams(MAX_DISTANCE_FILTER_UNITS.toString());
	};

	const handleFetchHealthSupportAccreditedUnits = async ({
		cityName,
		specialtyCode,
		stateCode,
		subSpecialtyCode,
		typeLocalName,
		signal,
	}: GetHealthProviderLocationSupportAccreditedParams) => {
		if (!cityName || !stateCode || !specialtyCode) {
			setUnitResponseState(prev => ({
				...prev,
				units: {
					...prev.units,
					unitsSupportAccredited: [],
				},
			}));

			return;
		}
		try {
			const healthSupportAccreditedUnits =
				await makeGetHealthProviderLocationSupportAccredited().get({
					specialtyCode: String(specialtyCode),
					subSpecialtyCode: subSpecialtyCode ? String(subSpecialtyCode) : '',
					stateCode: stateCode.toUpperCase(),
					cityName: cityName.toUpperCase(),
					typeLocalName: typeLocalName || TypeLocalNameEnum.CIDADE,
					signal,
				});

			if (!healthSupportAccreditedUnits) {
				setUnitResponseState(prev => ({
					...prev,
					units: {
						...prev.units,
						unitsSupportAccredited: [],
					},
				}));

				return;
			}

			const accreditedUnitsList = healthSupportAccreditedUnits.unidades?.find(
				units => units.rede === NetworkTypesEnum.ACCREDITED,
			)?.unidadesPorLocal;
			const supportUnitsList = healthSupportAccreditedUnits.unidades?.find(
				units => units.rede === NetworkTypesEnum.SUPPORT,
			)?.unidadesPorLocal;

			const parsedSupportUnitsList = supportUnitsList
				? parseHealthUnits({
						units: supportUnitsList as HealthProviderLocation[],
						isAccreditedV2Network: false,
					})
				: [];

			const parsedAccreditedUnitsList = accreditedUnitsList
				? parseHealthUnits({
						units: accreditedUnitsList as HealthProviderLocation[],
						isAccreditedV2Network: true,
					})
				: [];

			const parsedList = [...parsedSupportUnitsList, ...parsedAccreditedUnitsList];

			setUnitResponseState(prev => ({
				...prev,
				units: {
					...prev.units,
					unitsSupportAccredited: parsedList,
				},
			}));
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
		}
	};

	const checkHealthUnitsUpToMaxDistance = async (
		params: GetHealthProviderLocationPreviewParams,
	) => {
		setIsPreviewUnitsFetchedState(true);

		try {
			const {Content: hasUnitsUpToMaxDistance} =
				await makeGetHealthProviderLocationPreview().get(params);

			if (!hasUnitsUpToMaxDistance) {
				if (!isManualDistanceSearch) {
					const searchParams = new URLSearchParams(window.location.search);
					searchParams.set(
						QueryParamsEnum.LOCALITY_DISTANCE,
						MAX_DISTANCE_FILTER_UNITS.toString(),
					);
					setSearchParams(searchParams);
				}

				setHasUnitsUpToMaxDistanceState(false);
				return false;
			}

			return true;
		} catch {
			return true;
		}
	};

	return {
		IS_DONE,
		IS_ERROR,
		IS_LOADING,
		handleFetchUnits,
		unitResponse: unitResponseState,
		healthRequestSchedulePermission: healthRequestSchedulePermissionState,
		SHOW_NOT_FOUND,
		specialtyNotAvailableOnline: specialtyNotAvailableOnlineState,
		SHOW_NOT_FOUND_STATE,
		filteredNetworkList,
		shouldShowSupportAccreditedNetwork,
		shouldShowSelfNetwork,
		shouldShowEmptyAppointmentsCard,
		showSpecialtyNotAvailableInSelfNetwork,
	};
}
